import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { theme } from 'styles/theme';
import api from 'services/api';
import { showAlert } from 'utils/toastBridge';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Dialog = styled.div`
  width: min(520px, 100%);
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.md};
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  padding: 16px 18px 14px;
`;

const Title = styled.div`
  font-size: ${theme.typography.fontSize.md};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin-bottom: 8px;
`;

const Guide = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.55;
  margin-bottom: 12px;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.sm};
  padding: 10px 12px;
`;

const ResultBox = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  max-height: 220px;
  overflow: auto;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: 8px 10px;
  margin-top: 10px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
`;

const Btn = styled.button`
  border: 1px solid ${theme.colors.border};
  background: ${(p) => (p.$primary ? theme.colors.primary : theme.colors.white)};
  color: ${(p) => (p.$primary ? '#fff' : theme.colors.text.primary)};
  border-radius: ${theme.borderRadius.sm};
  padding: 6px 14px;
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

/**
 * zip + 사번파일명 사진 일괄등록 다이얼로그
 */
const PhotoBulkUploadDialog = ({ open, tenantCode, onClose, onDone }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const handleUpload = async () => {
    if (!file) {
      showAlert('zip 파일을 선택해 주세요.', 'warning');
      return;
    }
    if (!tenantCode) {
      showAlert('기관코드가 필요합니다.', 'warning');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('tenantCode', tenantCode);
      formData.append('file', file);
      const { data } = await api.postForm('/hr/employees/photos/bulk', formData);
      if (!(data.Msg === 'success' || data.success)) {
        showAlert(data.Msg || '일괄등록에 실패했습니다.', 'error');
        return;
      }
      setResult(data);
      showAlert(
        `사진 일괄등록 완료 — 성공 ${data.successCount || 0}건 / 실패 ${data.failCount || 0}건`,
        (data.failCount || 0) > 0 ? 'warning' : 'success'
      );
      onDone?.(data);
    } catch (e) {
      showAlert(e?.data?.Msg || e.message || '일괄등록에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={() => !loading && onClose?.()}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Title>사진 일괄등록 (zip)</Title>
        <Guide>
          <div>• zip 안에 <b>사번.확장자</b> 파일을 넣으세요. 예: <code>202410001.jpg</code></div>
          <div>• 지원: jpg, jpeg, png, webp, gif (파일당 500KB 이하)</div>
          <div>• 이미 등록된 사번만 반영되며, 기존 사진은 덮어씁니다.</div>
          <div>• 기관코드: <b>{tenantCode || '-'}</b></div>
        </Guide>

        <input
          ref={inputRef}
          type="file"
          accept=".zip,application/zip"
          style={{ display: 'none' }}
          onChange={(e) => {
            setFile(e.target.files?.[0] || null);
            setResult(null);
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Btn type="button" onClick={() => inputRef.current?.click()} disabled={loading}>
            zip 선택
          </Btn>
          <span style={{ fontSize: 13, color: theme.colors.text.secondary }}>
            {file ? file.name : '선택된 파일 없음'}
          </span>
        </div>

        {result ? (
          <ResultBox>
            <div>
              성공 {result.successCount ?? 0}건 / 실패 {result.failCount ?? 0}건
              {result.skippedCount ? ` / 스킵 ${result.skippedCount}` : ''}
            </div>
            {(result.failures || []).slice(0, 30).map((f, i) => (
              <div key={`${f.fileName}-${i}`} style={{ marginTop: 4, color: theme.colors.text.tertiary }}>
                · {f.fileName}: {f.reason}
              </div>
            ))}
            {(result.failures || []).length > 30 ? (
              <div style={{ marginTop: 4 }}>…외 {(result.failures || []).length - 30}건</div>
            ) : null}
          </ResultBox>
        ) : null}

        <Actions>
          <Btn type="button" onClick={onClose} disabled={loading}>
            닫기
          </Btn>
          <Btn type="button" $primary onClick={handleUpload} disabled={loading || !file}>
            {loading ? '처리 중…' : '업로드'}
          </Btn>
        </Actions>
      </Dialog>
    </Overlay>
  );
};

export default PhotoBulkUploadDialog;
